import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface Coupon {
    _id: string;
    coupon: string;
    type: string;
    fixedValue: number;
    createdAt: string;
}

export default function BackOfficeCoupons() {
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/coupons`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCoupons(response.data);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${CONFIG.apiUrl}/admin/coupons/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCoupons(coupons.filter((c) => c._id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting coupon:', error);
            alert('Failed to delete coupon');
        }
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Coupons d'affiliation
                    </div>
                    <button
                        onClick={() => navigate('/back-office/coupons/create')}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                        <MdAdd className="text-lg" />
                        Nouveau coupon
                    </button>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Code
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Type
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Valeur fixe
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Créé le
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {coupons.map((c) => (
                                    <tr key={c._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-4 font-mono text-sm font-semibold text-navy-700 dark:text-white">
                                            {c.coupon}
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {c.type}
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {c.fixedValue}
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {new Date(c.createdAt).toLocaleDateString('fr-FR')}
                                        </td>
                                        <td className="py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => navigate(`/back-office/coupons/edit/${c._id}`)}
                                                    className="rounded-lg bg-blue-500 p-2 text-white hover:bg-blue-600"
                                                    title="Edit"
                                                >
                                                    <MdEdit />
                                                </button>
                                                {deleteConfirm === c._id ? (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => handleDelete(c._id)}
                                                            className="rounded bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600"
                                                        >
                                                            Confirmer
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteConfirm(null)}
                                                            className="rounded bg-gray-500 px-3 py-1 text-xs text-white hover:bg-gray-600"
                                                        >
                                                            Annuler
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setDeleteConfirm(c._id)}
                                                        className="rounded-lg bg-red-500 p-2 text-white hover:bg-red-600"
                                                        title="Delete"
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {coupons.length === 0 && (
                            <div className="py-10 text-center text-gray-500">
                                Aucun coupon
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
